import { createAsyncThunk } from "@reduxjs/toolkit"
import { TestSpecification } from "../../testSpecification/testSpecificationApi"
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../http"
import { AppThunkAPIType } from "../../../app/appTypes"

export type DocumentTestOutcomeType =
    | 'UNKNOWN'
    | 'PASSED'
    | 'FAILED'
    | 'NOT_APPLICABLE'
    | 'PARTIALLYAPPROVED';

export type DocumentTestStatusEnum =
    | 'NEW'
    | 'PLANNED'
    | 'INPROGRESS'
    | 'COMPLETED'
    | 'CANCELLED';


export type DocumentTestRoleType =
    | 'TESTER'
    | 'APPROVER'
    | 'OBSERVER';

export type DocumentTestParentObjectType = 'URS' | 'RA'

export interface DocumentTest {
    id: number;
    guid: string
    sharedGuid: string;
    documentId: number;
    sectionId: string | null;
    sectionName?: string | null;
    order: number;
    plannedStart?: string | null;
    plannedEnd?: string | null;
    relatedObjectType: string | null
    relatedObjectId: number | null
    // Every object the test is linked to. `relatedObjectId` is the first of these;
    // read this instead, since one test can now cover several RA functions.
    relatedObjectIds: number[]

    parentObjectId: number | null
    parentObjectType: DocumentTestParentObjectType | null

    testSpecificationId: number;
    testSpecification: TestSpecification;
    status: DocumentTestStatusEnum;
    outcome: DocumentTestOutcomeType;

    lastRunId?: number | null;
    lastRunExecutedAt?: string | null;
    lastRunExecutedBy?: string | null;
    lastRunApprovedAt?: DocumentTestOutcomeType | null;
    lastRunApprovedBy?: string | null;
    lastRunOutcome?: DocumentTestOutcomeType | null;
    lastRunStatus?: DocumentTestStatusEnum | null;
    assignments?: DocumentTestAssignment[];
    runs: TestRun[]
    deviationId?: string | null
}

export interface TestRun {
    id: number
    documentTestId: number
    attemptNo: number
    status: string
    outcome: DocumentTestOutcomeType
    executedAt: string | null
    approvedAt: string | null
    comment: string
    participants: TestRunParticipant[]
    criteriaResults: TestCriteriaResult[]
}

export interface TestRunParticipant {
    id: number
    runId: number
    role: DocumentTestRoleType
    userName: string
    name: string
    position: string
    email: string
    companyName: string
    signatureId: string | null
}

export interface TestCriteriaResult {
    id: number
    runId: number
    criteriaId: number
    value: string
    passed: boolean
    comment: string
    criteriaNameSnapshot: string
}

export interface DocumentTestAssignment {
    id?: number | null;
    role: DocumentTestRoleType;
    userId?: string | null;
    name?: string | null;
    email?: string | null;
    companyName?: string | null;
}

export interface CreateDocumentTest {
    documentId: number;
    sectionId: string | null;
    plannedStart?: string | null;
    plannedEnd?: string | null;
    relatedObjectType: string | null
    relatedObjectId: number | null

    parentObjectId: string | null
    parentObjectType: DocumentTestParentObjectType | null

    testSpecification: Omit<TestSpecification, "id" | "structureId">

    assignments?: DocumentTestAssignment[];
}

export const loadDocumentSectionTests = createAsyncThunk<DocumentTest[], { documentId: number, sectionId: string }, AppThunkAPIType>(
    "document-tests/load-by-section",
    async ({ documentId, sectionId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/document-tests/${documentId}/sections/${sectionId}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentTest[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const loadDocumentAllTests = createAsyncThunk<DocumentTest[], { documentId: number }, AppThunkAPIType>(
    "document-tests/load-all-for-document",
    async ({ documentId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/document-tests/${documentId}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentTest[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)


export const createDocumentTest = createAsyncThunk<DocumentTest, CreateDocumentTest, AppThunkAPIType>(
    "document-tests/create",
    async (testData, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/document-tests/${testData.documentId}/add-test`,
            payload: testData,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentTest
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateDocumentTest = createAsyncThunk<DocumentTest, DocumentTest, AppThunkAPIType>(
    "document-tests/update",
    async (testData, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/document-tests/${testData.documentId}/update-test/${testData.id}`,
            payload: testData,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as DocumentTest
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

interface TestLinkArgs {
    documentId: number
    testId: number
    relatedObjectType: string
    relatedObjectId: number
}

// Linking is its own endpoint rather than part of update-test: that payload names
// a single related object, so saving through it would drop every other link.
export const linkTestRelatedObject = createAsyncThunk<DocumentTest, TestLinkArgs, AppThunkAPIType>(
    "document-tests/link-related",
    async ({ documentId, testId, relatedObjectType, relatedObjectId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/document-tests/${documentId}/tests/${testId}/related/${relatedObjectType}/${relatedObjectId}`,
            payload: {},
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentTest
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

// Removes one link only — the test and its runs survive even if it was the last.
//
// Resolves with the arguments rather than the response: deleteWithAuth never reads
// the body, so however much the endpoint returns, the payload here is undefined.
// The caller mirrors the removal instead, as softDeleteDocumentTest does.
export const unlinkTestRelatedObject = createAsyncThunk<TestLinkArgs, TestLinkArgs, AppThunkAPIType>(
    "document-tests/unlink-related",
    async (args, { dispatch, rejectWithValue }) => {
        const { documentId, testId, relatedObjectType, relatedObjectId } = args
        const result = await dispatch(deleteWithAuth({
            url: `api/document-tests/${documentId}/tests/${testId}/related/${relatedObjectType}/${relatedObjectId}`,
        }))
        const { payload } = result
        if (deleteWithAuth.fulfilled.match(result)) {
            return args
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const createTestRun = createAsyncThunk<DocumentTest, TestRun & { documentId: string }, AppThunkAPIType>(
    "document-tests/create-run",
    async (runData, { dispatch, rejectWithValue }) => {
        const { documentId, ...data } = runData
        const result = await dispatch(postWithAuth({
            url: `api/document-tests/${documentId}/add-test-run`,
            payload: data,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentTest
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateTestRun = createAsyncThunk<DocumentTest, TestRun & { documentId: string }, AppThunkAPIType>(
    "document-tests/update-run",
    async (runData, { dispatch, rejectWithValue }) => {
        const { documentId, ...data } = runData
        const result = await dispatch(postWithAuth({
            url: `api/document-tests/${documentId}/edit-test-run/${data.id}`,
            payload: data,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentTest
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const deleteTestRun = createAsyncThunk<DocumentTest, { documentId: string, runId: number }, AppThunkAPIType>(
    "document-tests/delete-run",
    async (args, { dispatch, rejectWithValue }) => {
        const { documentId, runId } = args
        const result = await dispatch(postWithAuth({
            url: `api/document-tests/${documentId}/delete-test-run/${runId}`,
            payload: {},
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentTest
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface SignArg {
    documentId: number
    runId: number
    participantId: number
    signature: string
    signedAt: string | null
}
export interface SignResult {
    runId: number
    participantId: number
    signatureId: string
    sign: string
    signedAt: string
}
export const signTestRun = createAsyncThunk<SignResult, SignArg, AppThunkAPIType>(
    "document-tests/sign-run",
    async (data, { dispatch, rejectWithValue }) => {
        const { documentId, runId, participantId, ...args } = data
        const result = await dispatch(postWithAuth({
            url: `api/document-tests/${documentId}/sign-test-run/${runId}/${participantId}`,
            payload: args,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as SignResult
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const softDeleteDocumentTest = createAsyncThunk<number, { documentId: number, testId: number }, AppThunkAPIType>("document-tests/deleteDocumentTest",
    async (data, { dispatch, rejectWithValue }) => {

        const result = await dispatch(deleteWithAuth({
            url: `api/document-tests/${data.documentId}/soft-delete-test/${data.testId}`,
        }))
        const { payload } = result

        if (deleteWithAuth.fulfilled.match(result)) {
            return data.testId

        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })
